import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {Vector3} from "@cm/math"
import {TemplateNode} from "#template/template-node/template-node"
import type {OnCompileContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {priorNodeClassChain} from "#template/versioning"
import type {EulerValue} from "#template/nodes/types"
import {eulerValue} from "#template/nodes/validation"
import type {EnvironmentImageData, SceneNodes} from "#template/interfaces/scene-object"
import {FetchHdriDataObject} from "#template/runtime-graph/nodes/fetch-hdri-data-object"
import {AppendDataObjectSize} from "#template/runtime-graph/nodes/append-data-object-size"
import {ThisStructID} from "#template/runtime-graph/types"
import {visitNone} from "#template/template-node/visit-helpers"
import {blenderEnvironmentRotation, platformEnvironmentRotation} from "#template/utils/environment-rotation"

export type HdriLightParameters = {
    name: string
    hdriId: number
    // as Blender's world Mapping turns the environment: about Z for the horizon, in degrees
    rotation: EulerValue
    intensity: number
    saturation: number
    clampHighlights?: number
    mirror: boolean
}

export type HdriLightConstructorParameters = Omit<HdriLightParameters, "saturation"> & {saturation?: number}

// templates saved at version 1 hold the renderer's rotation, about the platform's Y-up axes, which onCompile still hands it
const v1: Version<HdriLightParameters, HdriLightParameters> = {
    toNextVersion: (parameters) => ({...parameters, rotation: blenderEnvironmentRotation(parameters.rotation)}),
}

const hdriLightDefinition: TemplateNodeDefinition<HdriLightParameters, HdriLightConstructorParameters> = {
    nodeClass: "HdriLight",
    validation: {
        parameters: z.object({
            name: z.string(),
            hdriId: z.number(),
            rotation: eulerValue,
            intensity: z.number(),
            saturation: z._default(z.number(), 100),
            clampHighlights: z.optional(z.number()),
            mirror: z.boolean(),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([...priorNodeClassChain<HdriLightParameters>("HDRILight"), v1]),
}

@registerTemplateGraphNode
export class HdriLight extends TemplateNode<HdriLightParameters, HdriLightConstructorParameters> {
    public override get definition() {
        return hdriLightDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<HdriLightParameters> {
        const {evaluator, currentTemplate} = context
        const {templateContext} = evaluator
        const {sceneManager, computeDataObjectReferences} = templateContext
        const {preDisplayList} = currentTemplate
        const {clampHighlights, intensity, saturation, rotation, mirror, hdriId} = this.parameters

        const scope = evaluator.getScope(this)

        const dataObject = scope.node(FetchHdriDataObject, {sceneManager, hdriId}).dataObject

        const path = scope.pureLambda(scope.tuple(context.path, this), ([path, instance]) => [...path, instance], "newPath")

        const environment = scope.struct<SceneNodes.Environment>("Environment", {
            type: "Environment",
            id: ThisStructID,
            path,
            clampHighlights,
            intensity,
            saturation,
            rotation: scope.pureLambda(rotation, (rotation) => Vector3.fromArray(platformEnvironmentRotation(rotation)), "rotation"),
            mirror,
            envData: scope.struct<EnvironmentImageData>("EnvironmentImageData", {
                type: "dataObject",
                dataObject,
            }),
        })
        preDisplayList.push(environment)

        if (computeDataObjectReferences) {
            const referencedDataObject = scope.struct<SceneNodes.DataObjectReference>("DataObjectReference", {
                type: "DataObjectReference",
                id: `${scope.getPrefix()}:DataObjectReference@${hdriId}`,
                path,
                dataObject: scope.node(AppendDataObjectSize, {sceneManager, input: dataObject}).output,
                origin: {
                    type: "hdriLight",
                },
            })

            preDisplayList.push(referencedDataObject)
        }

        return visitNone(this.parameters)
    }
}
